import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, Redirect } from "react-router-dom";
import axios from "axios";
import {
  FaGraduationCap,
  FaUsers,
  FaSave,
  FaUserGraduate,
  FaListUl,
  FaExclamationTriangle,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import MdlSelectMultipleStudentFeeLocked from "../dialog/MdlSelectMultipleStudentFeeLocked";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/admission-dashboard.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

function studentInitials(name) {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

function resolveFeeAmount(fee) {
  if (fee?.student_fee !== undefined && fee?.student_fee !== null) {
    return parseFloat(fee.student_fee) || 0;
  }
  if (fee?.special_value !== undefined && fee?.special_value !== null) {
    return parseFloat(fee.special_value) || 0;
  }
  if (fee?.fee_value !== undefined && fee?.fee_value !== null) {
    return parseFloat(fee.fee_value) || 0;
  }
  return 0;
}

const StudentAvatar = memo(function StudentAvatar({ name, imagePath }) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [imagePath]);

  const showImg = Boolean(imagePath) && !imgFailed;

  return (
    <div className="rn-row__avatar" aria-hidden>
      {showImg ? (
        <img src={imagePath} alt="" onError={() => setImgFailed(true)} />
      ) : (
        <span className="rn-row__initials">{studentInitials(name)}</span>
      )}
    </div>
  );
});

const RteCreation = () => {
  const token = localStorage.getItem("token");
  const [loggedIn] = useState(token !== null);
  const [Loading, setLoading] = useState(false);
  const [MultipleStudentSelector, setMultipleStudentSelector] = useState(false);

  const [mSelectedStudents, setMSelectedStudents] = useState([]);
  const [mStudentsData, setMStudentsData] = useState([]);
  const [SESSION_ID, setSessionId] = useState("");

  const [editedFees, setEditedFees] = useState({});
  const [feeCollectionStatus, setFeeCollectionStatus] = useState({});
  const [submittingAll, setSubmittingAll] = useState(false);
  const [rteSelectedFees, setRteSelectedFees] = useState({});

  const mStudentsDataRef = useRef([]);
  const mSelectedStudentsRef = useRef([]);

  useEffect(() => {
    mStudentsDataRef.current = mStudentsData;
  }, [mStudentsData]);

  useEffect(() => {
    mSelectedStudentsRef.current = mSelectedStudents;
  }, [mSelectedStudents]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-select FEE003 (tuition fee) when students data is loaded
  useEffect(() => {
    if (!mStudentsData || mStudentsData.length === 0) return;

    mStudentsData.forEach((studentData, studentIndex) => {
      const fees = studentData.fees || [];
      const fee003Index = fees.findIndex((f) => f.fee_id === "FEE003");
      if (fee003Index < 0) return;

      const fee003Key = `${studentIndex}_${fee003Index}`;
      const fee003 = fees[fee003Index];

      setRteSelectedFees((prev) => {
        if (prev[fee003Key] || !fee003) return prev;
        return { ...prev, [fee003Key]: true };
      });

      setEditedFees((prev) => {
        if (prev[fee003Key] || !fee003) return prev;
        const enrollment_no =
          studentData.student?.student_enrollment_no ||
          studentData.student?.student_id;
        return {
          ...prev,
          [fee003Key]: {
            fee_id: fee003.fee_id,
            fee_value: 0,
            student_id: enrollment_no,
            class_id: studentData.student?.class_id,
            section_id: studentData.student?.section_id || "No",
            isSpecialCase: fee003.special_case === "Yes",
          },
        };
      });
    });
  }, [mStudentsData]);

  const HandleShow = useCallback(() => {
    setMultipleStudentSelector((prev) => !prev);
  }, []);

  const HandleClose = useCallback(() => {
    setMultipleStudentSelector(false);
  }, []);

  const processMultipleStudents = useCallback((students, session_id) => {
    setLoading(true);

    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const studentIds = students
      .map((s) => s.student_enrollment_no || s.student_id)
      .filter((id, index, self) => self.indexOf(id) === index);
    const classIds = [
      ...new Set(students.map((s) => s.class_id).filter((id) => id)),
    ];

    const collectionParams = {
      student_ids: studentIds.join(","),
      class_id: classIds[0] || "",
      session_id: session_id,
      school_code: SCHOOL_CODE,
    };

    const batchFeeParams = {
      student_ids: studentIds.join(","),
      class_ids: classIds.join(","),
      session_id: session_id,
      school_code: SCHOOL_CODE,
    };

    Promise.allSettled([
      axios
        .get(`${API_BASE()}checkIfStudentFeeCollectedBatch.php`, {
          params: collectionParams,
        })
        .then((res) => res.data)
        .catch((err) => {
          console.warn("Fee collection status fetch error:", err.message);
          return { api_status: "OK", data: [] };
        }),
      axios
        .get(`${API_BASE()}getStudentSpecialFeeBatch.php`, {
          params: batchFeeParams,
        })
        .then((res) => res.data)
        .catch((err) => {
          console.error("Fee structure fetch error:", err);
          throw err;
        }),
    ])
      .then(([collectionResult, feeResult]) => {
        const feeCollectionMap = {};
        if (
          collectionResult.status === "fulfilled" &&
          collectionResult.value?.api_status === "OK"
        ) {
          const collectionData = collectionResult.value.data;
          if (Array.isArray(collectionData)) {
            collectionData.forEach((item) => {
              const enrollment_no =
                item.student_id || item.student_enrollment_no;
              if (enrollment_no) {
                feeCollectionMap[enrollment_no] =
                  item.fee_collected === true || item.fee_collected === 1;
              }
            });
          } else if (collectionData && typeof collectionData === "object") {
            Object.keys(collectionData).forEach((enrollment_no) => {
              feeCollectionMap[enrollment_no] =
                collectionData[enrollment_no] === true ||
                collectionData[enrollment_no] === 1;
            });
          }
        }

        if (feeResult.status === "rejected") {
          throw feeResult.reason;
        }

        const feeResponse = feeResult.value;
        if (
          !feeResponse ||
          feeResponse.api_status !== "OK" ||
          !feeResponse.data
        ) {
          throw new Error("Invalid response from getStudentSpecialFeeBatch");
        }

        const feeDataMap = feeResponse.data;

        return students.map((student) => {
          const enrollment_no =
            student.student_enrollment_no || student.student_id;
          const student_class_id = student.class_id;
          const fees = feeDataMap[enrollment_no] || [];
          const studentFeeStatus = {};
          const isFeeCollected = feeCollectionMap[enrollment_no] || false;
          fees.forEach((fee) => {
            studentFeeStatus[
              `${enrollment_no}_${student_class_id}_${fee.fee_id}`
            ] = isFeeCollected;
          });

          return {
            student: {
              ...student,
              student_enrollment_no: enrollment_no,
              student_id: enrollment_no,
              class_id: student_class_id,
              section_id: student.section_id || "No",
            },
            fees,
            feeCollected: feeCollectionMap[enrollment_no] || false,
            feeStatus: studentFeeStatus,
          };
        });
      })
      .then((studentsData) => {
        setLoading(false);
        if (studentsData && studentsData.length > 0) {
          setMStudentsData(studentsData);
          const allFeeStatus = {};
          studentsData.forEach((studentData) => {
            if (studentData?.feeStatus) {
              Object.assign(allFeeStatus, studentData.feeStatus);
            }
          });
          setFeeCollectionStatus(allFeeStatus);
        } else {
          myToast.notify_warning("No fee data found for selected students");
          setMStudentsData([]);
          setFeeCollectionStatus({});
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error in processMultipleStudents:", error);
        const errorMessage =
          error.response?.data?.api_remark ||
          error.message ||
          "Error fetching student data. Please try again.";
        myToast.notify_error(errorMessage);
        setMStudentsData([]);
        setFeeCollectionStatus({});
      });
  }, []);

  const HandleSelectedMultipleStudents = useCallback(
    (selectedStudents) => {
      if (!selectedStudents || selectedStudents.length === 0) {
        myToast.notify_warning("Please select at least one student");
        return;
      }

      const firstStudent = selectedStudents[0];
      let session_id = firstStudent.session_id;
      if (!session_id) {
        const sessionData = SessionManager.getSessionDetails();
        session_id =
          sessionData?.session_id || SessionManager.getSessionId();
      }

      setMultipleStudentSelector(false);
      setMSelectedStudents(selectedStudents);
      setSessionId(session_id);
      setMStudentsData([]);
      setEditedFees({});
      setRteSelectedFees({});
      processMultipleStudents(selectedStudents, session_id);
    },
    [processMultipleStudents]
  );

  const handleFeeValueChange = useCallback(
    (studentIndex, feeIndex, fee, newValue) => {
      const key = `${studentIndex}_${feeIndex}`;
      const enrollment_no =
        mStudentsData[studentIndex]?.student?.student_enrollment_no ||
        mStudentsData[studentIndex]?.student?.student_id;
      const class_id = mStudentsData[studentIndex]?.student?.class_id;
      const section_id =
        mStudentsData[studentIndex]?.student?.section_id || "No";

      let feeValue = newValue;
      if (newValue === "" || newValue === null || newValue === undefined) {
        feeValue = "";
      } else {
        feeValue =
          typeof newValue === "number"
            ? newValue
            : parseFloat(newValue) || 0;
      }

      setEditedFees((prev) => ({
        ...prev,
        [key]: {
          fee_id: fee.fee_id,
          fee_value: feeValue,
          student_id: enrollment_no,
          class_id: class_id,
          section_id: section_id,
          isSpecialCase: fee.special_case === "Yes",
        },
      }));
    },
    [mStudentsData]
  );

  const handleRteCheckboxChange = useCallback(
    (studentIndex, feeIndex, fee, checked) => {
      const key = `${studentIndex}_${feeIndex}`;
      setRteSelectedFees((prev) => ({
        ...prev,
        [key]: checked,
      }));

      if (checked) {
        setEditedFees((prev) => {
          const current = prev[key];
          if (
            current &&
            current.fee_value !== undefined &&
            current.fee_value !== null &&
            current.fee_value !== ""
          ) {
            return prev;
          }
          const enrollment_no =
            mStudentsDataRef.current[studentIndex]?.student
              ?.student_enrollment_no ||
            mStudentsDataRef.current[studentIndex]?.student?.student_id;
          return {
            ...prev,
            [key]: {
              fee_id: fee.fee_id,
              fee_value: 0,
              student_id: enrollment_no,
              class_id:
                mStudentsDataRef.current[studentIndex]?.student?.class_id,
              section_id:
                mStudentsDataRef.current[studentIndex]?.student
                  ?.section_id || "No",
              isSpecialCase: fee.special_case === "Yes",
            },
          };
        });
      }
    },
    []
  );

  //Created By Cursor — enable RTE + set amount in one tap (Free / Half / Full)
  const applyRteFeePreset = useCallback(
    (studentIndex, feeIndex, fee, amount) => {
      const key = `${studentIndex}_${feeIndex}`;
      const student = mStudentsDataRef.current[studentIndex]?.student;
      const enrollment_no =
        student?.student_enrollment_no || student?.student_id;

      setRteSelectedFees((prev) => ({ ...prev, [key]: true }));
      setEditedFees((prev) => ({
        ...prev,
        [key]: {
          fee_id: fee.fee_id,
          fee_value: amount,
          student_id: enrollment_no,
          class_id: student?.class_id,
          section_id: student?.section_id || "No",
          isSpecialCase: fee.special_case === "Yes",
        },
      }));
    },
    []
  );

  const submitAllFees = useCallback(async () => {
    const editedKeys = Object.keys(editedFees).filter(
      (key) => rteSelectedFees[key]
    );

    if (editedKeys.length === 0) {
      myToast.notify_warning(
        "No RTE fees selected to submit. Please check RTE checkbox for fees you want to submit."
      );
      return;
    }

    for (const key of editedKeys) {
      const [studentIndex, feeIndex] = key.split("_").map(Number);
      const studentData = mStudentsData[studentIndex];
      if (!studentData) continue;
      const fee = studentData.fees[feeIndex];
      if (!fee) continue;
      const enrollment_no =
        studentData.student?.student_enrollment_no ||
        studentData.student?.student_id;
      const class_id = studentData.student?.class_id;
      const feeKey = `${enrollment_no}_${class_id}_${fee.fee_id}`;
      if (feeCollectionStatus[feeKey]) {
        myToast.notify_warning(
          `Cannot submit: Fee collection or settlement is already done for ${fee.fee_name}.`
        );
        return;
      }
    }

    const mStaffData = SessionManager.getStaffDetails();
    const STAFF_ID = mStaffData?.staff_id;
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    if (!STAFF_ID || !SCHOOL_CODE || !SESSION_ID) {
      myToast.notify_warning("Staff, school, or session is missing.");
      return;
    }

    const feesArray = editedKeys
      .map((key) => {
        const [studentIndex, feeIndex] = key.split("_").map(Number);
        const editedFee = editedFees[key];
        const studentData = mStudentsData[studentIndex];
        const fee = studentData?.fees[feeIndex];
        if (!fee || !editedFee) return null;
        return {
          student_id: editedFee.student_id,
          class_id: editedFee.class_id,
          section_id: editedFee.section_id,
          fee_id: editedFee.fee_id,
          fee_value: editedFee.fee_value === "" ? 0 : editedFee.fee_value,
          fee_value_old:
            fee.student_fee ?? fee.special_value ?? fee.fee_value ?? 0,
        };
      })
      .filter((f) => f !== null);

    if (feesArray.length === 0) {
      myToast.notify_warning("No valid fees to submit");
      return;
    }

    setSubmittingAll(true);
    try {
      const body = new URLSearchParams({
        staff_id: String(STAFF_ID),
        session_id: String(SESSION_ID),
        school_code: String(SCHOOL_CODE),
        fees: JSON.stringify(feesArray),
      });

      let payload = null;
      try {
        const res = await axios.post(
          `${API_BASE()}updateRteFeeBatch.php`,
          body,
          { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        );
        payload = res?.data;
      } catch (postError) {
        console.warn(
          "[RteCreation] Submit All POST failed, trying GET",
          postError
        );
        const res = await axios.get(`${API_BASE()}updateRteFeeBatch.php`, {
          params: {
            staff_id: String(STAFF_ID),
            session_id: String(SESSION_ID),
            school_code: String(SCHOOL_CODE),
            fees: JSON.stringify(feesArray),
          },
        });
        payload = res?.data;
      }

      if (payload?.api_status === "OK" && payload.data) {
        const { success, failed, total } = payload.data;
        if (success > 0) {
          myToast.notify_success(
            `Successfully saved ${success} out of ${total} fee(s)`
          );
        }
        if (failed > 0) {
          myToast.notify_warning(`Failed to save ${failed} fee(s)`);
        }
        setEditedFees({});
        setRteSelectedFees({});
        processMultipleStudents(mSelectedStudentsRef.current, SESSION_ID);
      } else {
        myToast.notify_error(payload?.api_remark || "Failed to submit fees");
      }
    } catch (error) {
      console.error("Error submitting fees:", error);
      myToast.notify_error("Error submitting fees. Please try again.");
    } finally {
      setSubmittingAll(false);
    }
  }, [
    editedFees,
    mStudentsData,
    SESSION_ID,
    feeCollectionStatus,
    processMultipleStudents,
    rteSelectedFees,
  ]);

  const rteSelectedCount = useMemo(
    () => Object.values(rteSelectedFees).filter(Boolean).length,
    [rteSelectedFees]
  );

  const dirtyCount = useMemo(() => {
    return Object.keys(editedFees).filter((key) => rteSelectedFees[key])
      .length;
  }, [editedFees, rteSelectedFees]);

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  const busy = Loading || submittingAll;

  const submitButton = (
    <button
      type="button"
      className="rn-btn rn-btn--solid"
      onClick={submitAllFees}
      disabled={busy || dirtyCount === 0}
    >
      <span className="rn-btn__icon" aria-hidden>
        <FaSave />
      </span>
      <span className="rn-btn__text">
        <span className="rn-btn__title">
          {submittingAll
            ? "Saving…"
            : dirtyCount > 0
            ? `Submit All (${dirtyCount})`
            : "Submit All"}
        </span>
        <span className="rn-btn__hint">
          {dirtyCount > 0 ? "Save RTE fee selections" : "No RTE fees ready"}
        </span>
      </span>
    </button>
  );

  return (
    <div
      className="content payroll-page sm-page rn-page rtec-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Add RTE</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaGraduationCap />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Add RTE</h1>
              <p className="sm-hero__sub">
                {mStudentsData.length
                  ? `${mStudentsData.length} student${
                      mStudentsData.length === 1 ? "" : "s"
                    } · mark fees as RTE`
                  : "Select students, then set RTE fee values"}
                {dirtyCount > 0 ? ` · ${dirtyCount} unsaved` : ""}
              </p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <Link to="/AdminMain/RteStudent" className="rtec-hero-link">
              <FaListUl aria-hidden /> RTE List
            </Link>
            <button
              type="button"
              className="rn-btn rn-btn--solid rtec-select-btn"
              onClick={HandleShow}
              disabled={busy}
            >
              <span className="rn-btn__icon" aria-hidden>
                <FaUsers />
              </span>
              <span className="rn-btn__text">
                <span className="rn-btn__title">
                  {mStudentsData.length
                    ? "Change Students"
                    : "Select Students"}
                </span>
                <span className="rn-btn__hint">Open student picker</span>
              </span>
            </button>
          </div>
        </header>

        <div className="rn-content">
          {Loading ? (
            <div className="rn-loading">
              <MyLoader />
            </div>
          ) : mStudentsData.length === 0 ? (
            <div className="sm-empty">
              <FaUserGraduate className="rn-empty-icon" aria-hidden />
              <p className="sm-empty__title">No students selected</p>
              <p className="sm-empty__hint">
                Click Select Students to begin adding RTE fees
              </p>
            </div>
          ) : (
            <>
              <div className="rn-toolbar rn-toolbar--top">
                <div className="rn-toolbar__status" aria-live="polite">
                  <span className="rn-status-chip rn-status-chip--students">
                    {mStudentsData.length} student
                    {mStudentsData.length === 1 ? "" : "s"}
                  </span>
                  <span className="rn-status-chip rn-status-chip--students">
                    {rteSelectedCount} RTE fee
                    {rteSelectedCount === 1 ? "" : "s"} marked
                  </span>
                  {dirtyCount > 0 ? (
                    <span className="rn-status-chip rn-status-chip--unsaved">
                      {dirtyCount} unsaved
                    </span>
                  ) : (
                    <span className="rn-status-chip rn-status-chip--clean">
                      Up to date
                    </span>
                  )}
                </div>
                <div className="rn-toolbar__actions">{submitButton}</div>
              </div>

              <div className="rtec-list">
                {mStudentsData.map((studentData, studentIndex) => {
                  const student = studentData.student;
                  const fees = studentData.fees || [];
                  const feeCollected = studentData.feeCollected;
                  const enrollment_no =
                    student.student_enrollment_no || student.student_id;
                  const classLabel =
                    student.section_id === "No"
                      ? student.class_name
                      : `${student.class_name || ""}:${
                          student.section_name || ""
                        }`;

                  const sortedFees = [...fees].sort((a, b) => {
                    if (a.fee_id === "FEE003") return -1;
                    if (b.fee_id === "FEE003") return 1;
                    return 0;
                  });

                  return (
                    <article
                      key={enrollment_no}
                      className={`rtec-card${
                        feeCollected ? " rtec-card--locked" : ""
                      }`}
                      style={{ "--rn-i": studentIndex }}
                    >
                      <div className="rtec-card__who">
                        <StudentAvatar
                          name={student.stu_name}
                          imagePath={student.student_img_path}
                        />
                        <div className="min-w-0">
                          <div className="rn-row__name">
                            {student.stu_name || "—"}
                          </div>
                          <div className="rn-row__chips">
                            <span className="rn-chip rn-chip--father">
                              {student.father_name
                                ? `S/O ${student.father_name}`
                                : "—"}
                            </span>
                            {classLabel ? (
                              <span className="rn-chip rn-chip--class">
                                {classLabel}
                              </span>
                            ) : null}
                            {student.student_roll_no ? (
                              <span className="rn-chip rn-chip--enroll">
                                Roll: {student.student_roll_no}
                              </span>
                            ) : null}
                            <span className="rn-chip rn-chip--enroll">
                              ID: {enrollment_no}
                            </span>
                            {student.sr_admission_no ? (
                              <span className="rn-chip rn-chip--enroll">
                                SRN: {student.sr_admission_no}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      {feeCollected ? (
                        <div className="rtec-lock-banner" role="alert">
                          <FaExclamationTriangle aria-hidden />
                          <span>
                            Fee collection found for this student. Cannot
                            create/update RTE fee.
                          </span>
                        </div>
                      ) : sortedFees.length === 0 ? (
                        <div className="rtec-empty-fees">No fees found</div>
                      ) : (
                        <div className="rtec-fees">
                          {sortedFees.map((item2, index2) => {
                            const originalIndex = fees.findIndex(
                              (f) => f.fee_id === item2.fee_id
                            );
                            const actualIndex =
                              originalIndex >= 0 ? originalIndex : index2;
                            const feeKey = `${enrollment_no}_${student.class_id}_${item2.fee_id}`;
                            const feeCollectedRow =
                              feeCollectionStatus[feeKey] || false;
                            const editKey = `${studentIndex}_${actualIndex}`;
                            const editedFee = editedFees[editKey];
                            const isRteSelected =
                              rteSelectedFees[editKey] || false;
                            const originalValue = resolveFeeAmount(item2);
                            const classFee = parseFloat(item2.fee_value) || 0;
                            const halfFee = Math.round(classFee * 0.5);

                            let currentValue = originalValue;
                            if (editedFee !== undefined) {
                              if (
                                editedFee.fee_value === "" ||
                                editedFee.fee_value === null ||
                                editedFee.fee_value === undefined
                              ) {
                                currentValue = "";
                              } else {
                                currentValue =
                                  parseFloat(editedFee.fee_value) || 0;
                              }
                            }

                            const editedNumericValue =
                              editedFee !== undefined &&
                              editedFee.fee_value !== "" &&
                              editedFee.fee_value !== null &&
                              editedFee.fee_value !== undefined
                                ? parseFloat(editedFee.fee_value) || 0
                                : originalValue;
                            const hasChanges =
                              editedFee !== undefined &&
                              (editedNumericValue !== originalValue ||
                                isRteSelected);
                            const valueForPreset =
                              currentValue === "" ? null : Number(currentValue);
                            const presetFree = valueForPreset === 0;
                            const presetHalf = valueForPreset === halfFee;
                            const presetFull = valueForPreset === classFee;

                            return (
                              <div
                                key={`${item2.fee_id}_${index2}`}
                                className={`rtec-fee${
                                  isRteSelected ? " rtec-fee--selected" : ""
                                }${
                                  hasChanges && isRteSelected
                                    ? " rtec-fee--dirty"
                                    : ""
                                }${
                                  feeCollectedRow ? " rtec-fee--locked" : ""
                                }`}
                              >
                                <label className="rtec-fee__switch">
                                  <input
                                    type="checkbox"
                                    checked={isRteSelected}
                                    onChange={(e) =>
                                      handleRteCheckboxChange(
                                        studentIndex,
                                        actualIndex,
                                        item2,
                                        e.target.checked
                                      )
                                    }
                                    disabled={feeCollectedRow}
                                    aria-label={`Include ${item2.fee_name} in RTE`}
                                  />
                                  <span className="rtec-fee__switch-ui" aria-hidden />
                                </label>

                                <div className="rtec-fee__identity">
                                  <span className="rtec-fee__name">
                                    {isRteSelected
                                      ? `${item2.fee_name} (RTE)`
                                      : item2.fee_name}
                                  </span>
                                  <span className="rtec-fee__class">
                                    ₹{classFee.toLocaleString("en-IN")}
                                  </span>
                                </div>

                                {feeCollectedRow ? (
                                  <span className="rtec-fee__locked-tag">
                                    Locked
                                  </span>
                                ) : isRteSelected ? (
                                  <>
                                    <div
                                      className="rtec-fee__seg"
                                      role="group"
                                      aria-label="RTE amount presets"
                                    >
                                      <button
                                        type="button"
                                        className={`rtec-seg${
                                          presetFree ? " is-active" : ""
                                        }`}
                                        onClick={() =>
                                          applyRteFeePreset(
                                            studentIndex,
                                            actualIndex,
                                            item2,
                                            0
                                          )
                                        }
                                      >
                                        Free
                                      </button>
                                      <button
                                        type="button"
                                        className={`rtec-seg${
                                          presetHalf ? " is-active" : ""
                                        }`}
                                        onClick={() =>
                                          applyRteFeePreset(
                                            studentIndex,
                                            actualIndex,
                                            item2,
                                            halfFee
                                          )
                                        }
                                        title={`₹${halfFee.toLocaleString(
                                          "en-IN"
                                        )}`}
                                      >
                                        50%
                                      </button>
                                      <button
                                        type="button"
                                        className={`rtec-seg${
                                          presetFull ? " is-active" : ""
                                        }`}
                                        onClick={() =>
                                          applyRteFeePreset(
                                            studentIndex,
                                            actualIndex,
                                            item2,
                                            classFee
                                          )
                                        }
                                        title={`₹${classFee.toLocaleString(
                                          "en-IN"
                                        )}`}
                                      >
                                        Full
                                      </button>
                                    </div>

                                    <div className="rtec-fee__input">
                                      <span aria-hidden>₹</span>
                                      <input
                                        id={`rtec-${editKey}`}
                                        type="number"
                                        min="0"
                                        step="1"
                                        inputMode="numeric"
                                        value={
                                          currentValue === null ||
                                          currentValue === undefined ||
                                          currentValue === ""
                                            ? ""
                                            : currentValue
                                        }
                                        onChange={(e) => {
                                          const inputValue = e.target.value;
                                          if (
                                            inputValue === "" ||
                                            inputValue === null
                                          ) {
                                            handleFeeValueChange(
                                              studentIndex,
                                              actualIndex,
                                              item2,
                                              ""
                                            );
                                            return;
                                          }
                                          const numValue =
                                            parseFloat(inputValue);
                                          if (!isNaN(numValue)) {
                                            handleFeeValueChange(
                                              studentIndex,
                                              actualIndex,
                                              item2,
                                              numValue
                                            );
                                          }
                                        }}
                                        onBlur={(e) => {
                                          const inputValue =
                                            e.target.value.trim();
                                          if (
                                            inputValue === "" ||
                                            inputValue === null
                                          ) {
                                            handleFeeValueChange(
                                              studentIndex,
                                              actualIndex,
                                              item2,
                                              0
                                            );
                                          } else {
                                            const numValue =
                                              parseFloat(inputValue);
                                            if (isNaN(numValue)) {
                                              handleFeeValueChange(
                                                studentIndex,
                                                actualIndex,
                                                item2,
                                                originalValue
                                              );
                                            } else {
                                              handleFeeValueChange(
                                                studentIndex,
                                                actualIndex,
                                                item2,
                                                Math.max(0, numValue)
                                              );
                                            }
                                          }
                                        }}
                                        disabled={feeCollectedRow}
                                        placeholder="0"
                                        aria-label={`${item2.fee_name} RTE amount`}
                                      />
                                    </div>
                                  </>
                                ) : (
                                  <span className="rtec-fee__hint">Off</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>

              <div className="rn-toolbar rn-toolbar--bottom">
                <div className="rn-toolbar__actions">{submitButton}</div>
              </div>
            </>
          )}
        </div>
      </div>

      {MultipleStudentSelector ? (
        <MdlSelectMultipleStudentFeeLocked
          actionClose={HandleClose}
          actionHandleSelected={HandleSelectedMultipleStudents}
          title="Select Students for RTE"
        />
      ) : null}
    </div>
  );
};

export default RteCreation;
